import { ContentService, type TopicCreate, type TopicPublic } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import { toaster } from "@/components/ui/toaster"
import { useMutation, useQueryClient } from "@tanstack/react-query"

interface CreateTopicsResult {
  createdTopics: TopicPublic[]
  failedTopics: string[]
}

export function useCreateTopics() {
  const queryClient = useQueryClient()

  const createSingleTopic = async (name: string): Promise<TopicPublic> => {
    return await ContentService.createTopic({
      requestBody: { name } as TopicCreate,
    })
  }

  const createMultipleTopics = async (
    topicNames: string[],
  ): Promise<CreateTopicsResult> => {
    const createdTopics: TopicPublic[] = []
    const failedTopics: string[] = []

    for (const name of topicNames) {
      try {
        const topic = await createSingleTopic(name)
        createdTopics.push(topic)
        // Show success toast for each topic
        toaster.create({
          title: "Topic added",
          description: `"${topic.name}" has been created and added to the question.`,
          type: "success",
        })
      } catch (error) {
        failedTopics.push(name)
        toaster.create({
          title: "Failed to create topic",
          description: `Could not create "${name}". ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          type: "error",
        })
      }
    }

    return { createdTopics, failedTopics }
  }

  return useMutation({
    mutationFn: createMultipleTopics,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["topics-autocomplete"] })
    },
    onError: (err: ApiError) => {
      toaster.create({
        title: "Error creating topics",
        description: err.message || "An error occurred while creating topics.",
        type: "error",
      })
    },
  })
}
